import React from 'react';
import { Form, Card, Message, Icon, Table, Section, ToggleLabel, Button, Tooltip, Modal, ToastContext, Loader } from '@weezevent/nacre';
import { useTranslation } from 'react-i18next';
import { usePermissions } from '../../../user/permissions';
import { useHistory } from 'react-router-dom';
import { stylizeIdentityOption } from '../../waitingList/details/waitingListForm';
import ModalSwappableConfigRule from './modalSwappableConfigRule';
import SwappableRateConfig from '../../../models/swappableRateConfig';
import IdentityModel from '../../../models/identity';
import MailTemplateModel from '../../../models/mailTemplate';
import TicketTransfer from './ticketTransfer';
import Style from './ticketManagement.module.css';

function SwappableConfigRules({ swappableConfigs, setConfigToDelete, setModalConfig }) {
    const { t } = useTranslation();

    return (
        <div className={Style['swappable-config-rules-container']}>
            <div className={Style['swappable-config-rules-title']}>{t('ticket.rates.ticket-management.swappable-config-rules.header', { count: swappableConfigs.length })}</div>
            <Table>
                <Table.THead>
                    <Table.Tr isHeader>
                        <th className={Style['width-x-large']}>{t('ticket.rates.ticket-management.swappable-config-rules.name')}</th>
                        <th className={Style['width-x-mini']}>{t('ticket.rates.ticket-management.swappable-config-rules.actions')}</th>
                    </Table.Tr>
                </Table.THead>
                <Table.TBody>
                    {swappableConfigs.map(config => (
                        <Table.Tr key={config.id}>
                            <Table.Td>{config.name}</Table.Td>
                            <Table.Td className={Style['table-actions']}>
                                <Tooltip triggerOnHover title={t('ticket.common.action.edit')}>
                                    <Icon name="cog" onClick={() => setModalConfig(config)} />
                                </Tooltip>
                                <Tooltip triggerOnHover title={t('ticket.common.action.delete')}>
                                    <Icon name="trash" onClick={() => setConfigToDelete(config)} />
                                </Tooltip>
                            </Table.Td>
                        </Table.Tr>
                    ))}
                </Table.TBody>
            </Table>
        </div>
    );
}

function SwappableConfig({ swappableConfigs, setConfigToDelete, setModalConfig }) {
    const { t } = useTranslation();

    const [isChecked, setIsChecked] = React.useState(swappableConfigs.length > 0 ? true : false);

    const onToggle = React.useCallback(
        ({ checked }) => {
            if (!checked && swappableConfigs.length > 0) {
                setConfigToDelete(swappableConfigs);
            } else {
                setIsChecked(prevState => !prevState);
            }
        },
        [swappableConfigs]
    );

    const onClick = React.useCallback(
        e => {
            e.preventDefault();
            onToggle(isChecked);
        },
        [onToggle, isChecked]
    );

    return (
        <>
            <span onClick={onClick}>
                <ToggleLabel
                    inlinePosition="left"
                    checked={isChecked}
                    onToggle={() => setIsChecked(prevState => !prevState)}
                    className={Style['swappable-config-container']}
                    label={t('ticket.rates.ticket-management.swappable-config.title')}
                    subtitle={t('ticket.rates.ticket-management.swappable-config.subtitle')}
                />
            </span>
            {isChecked && (
                <Section>
                    <Message type="default" title={t('ticket.rates.ticket-management.swappable-config.message-title')}>
                        {t('ticket.rates.ticket-management.swappable-config.message-subtitle')}
                    </Message>
                    {swappableConfigs.length > 0 && (
                        <SwappableConfigRules swappableConfigs={swappableConfigs} setConfigToDelete={setConfigToDelete} setModalConfig={setModalConfig} />
                    )}
                    <Button
                        primary
                        inverted
                        onClick={() => setModalConfig(true)}
                        label={
                            <span>
                                <Icon name="plus" />
                                {t('ticket.rates.ticket-management.swappable-config.cta')}
                            </span>
                        }
                    />
                </Section>
            )}
        </>
    );
}

export default function TicketManagement({ organization, event, rate, isAllowedTicketManagement, form, setForm }) {
    const { t } = useTranslation();
    const permissions = usePermissions();
    const history = useHistory();
    const toast = React.useContext(ToastContext);

    const [swappableConfigs, setSwappableConfigs] = React.useState([]);
    const [isSubmitting, setIsSubmitting] = React.useState(false);

    const [modalConfig, setModalConfig] = React.useState(null);

    const [configToDelete, setConfigToDelete] = React.useState(null);

    const viewPermissions = React.useMemo(() => {
        const hasPersonalSpaceAdminCapacity = Boolean(permissions.hasCapacities(['PyvarCapacity.PERSONALSPACE_ADMIN'], organization?.id));
        return {
            canAllowRateExchange: hasPersonalSpaceAdminCapacity
        };
    }, [organization?.id, permissions]);

    // eslint-disable-next-line no-unused-vars
    const [_, loadingSwappableConfig, syncSwappableConfig] = SwappableRateConfig.useApiModel(
        {
            organizationId: organization.id,
            eventId: event?.id,
            rateId: rate.id,
            cache: false,
            allow_cache: false,
            defaultLoading: false,
            launch: Boolean(isAllowedTicketManagement && organization?.id && event?.id && rate?.id),
            onSuccess: configs => setSwappableConfigs(configs)
        },
        [organization, event, rate, isAllowedTicketManagement]
    );

    const [identities, loadingIdentities] = IdentityModel.useApiModel(
        {
            organizationId: organization.id
        },
        [organization]
    );

    const [emailTemplates, loadingEmailTemplates] = MailTemplateModel.useApiModel(
        {
            organizationId: organization.id,
            key: 'crm-mail'
        },
        [organization]
    );

    const identitiesOptions = React.useMemo(() => {
        let sortedIdentities = identities?.sort((a, b) => a.from_address.name.toLowerCase().localeCompare(b.from_address.name.toLowerCase())) || [];
        sortedIdentities = sortedIdentities.map(e => {
            return {
                key: e.id,
                text: stylizeIdentityOption(e, t),
                value: e.id
            };
        });
        // identity by default option --> Weezevent
        const defaultValue = {
            key: 0,
            text: t('ticket.waiting_list.create.emails_and_notifications.default_identity'),
            value: null
        };
        // default option is placed as first option
        sortedIdentities.unshift(defaultValue);
        return sortedIdentities;
    }, [identities]);

    const templatesOptions = React.useMemo(() => {
        if (!emailTemplates) return [];
        let _templatesOptions = [
            {
                key: 0,
                value: null,
                text: t('ticket.waiting_list.create.emails_and_notifications.default_email')
            }
        ];
        emailTemplates &&
            emailTemplates.forEach(e => {
                _templatesOptions.push({
                    key: e.id,
                    text: e.name,
                    value: e.id
                });
            });
        return _templatesOptions;
    }, [emailTemplates]);

    const deleteRule = React.useCallback(() => {
        if (!configToDelete) return;
        setIsSubmitting(true);

        const params = {
            organizationId: organization.id,
            eventId: event?.id ?? null,
            rateId: rate.id
        };

        const configsArray = Array.isArray(configToDelete) ? configToDelete : [configToDelete];

        SwappableRateConfig.perform_delete(
            configsArray.map(config => config.id),
            params
        )
            .then(() => {
                toast.success(t('ticket.rates.ticket-management.delete-modal.toast-success', { count: configsArray.length }));
                if (!Array.isArray(configToDelete)) {
                    setSwappableConfigs(prevConfigs => prevConfigs.filter(config => config.id !== configToDelete.id));
                }
            })
            .catch(error => {
                console.error('Error deleting rules:', error);
                toast.error(t('ticket.rates.ticket-management.delete-modal.toast-error', { count: configsArray.length }));
            })
            .finally(() => {
                setConfigToDelete(null);
                syncSwappableConfig();
                setIsSubmitting(false);
            });
    }, [organization, event, rate, configToDelete, syncSwappableConfig, t]);

    if (loadingSwappableConfig || loadingIdentities || loadingEmailTemplates) {
        return <Loader />;
    }

    return (
        <>
            <Modal
                allowExternalClosing={false}
                open={Boolean(configToDelete)}
                onClose={() => setConfigToDelete(null)}
                title={Array.isArray(configToDelete) ? t('ticket.rates.ticket-management.delete-modal.toogle-title') : t('ticket.rates.ticket-management.delete-modal.title')}
                subtitle={Array.isArray(configToDelete) ? '' : t('ticket.rates.ticket-management.delete-modal.subtitle')}
                submitButton={
                    <Button
                        primary
                        label={Array.isArray(configToDelete) ? t('ticket.rates.ticket-management.delete-modal.toogle-save') : t('ticket.common.action.confirm')}
                        onClick={deleteRule}
                        isLoading={isSubmitting}
                    />
                }
                cancelButton={<Button label={t('ticket.common.action.cancel')} onClick={() => setConfigToDelete(null)} />}
            >
                <Message type="warning" title={t('ticket.rates.ticket-management.delete-modal.message-title')}>
                    {Array.isArray(configToDelete)
                        ? t('ticket.rates.ticket-management.delete-modal.toogle-message-subtitle')
                        : t('ticket.rates.ticket-management.delete-modal.message-subtitle')}
                </Message>
            </Modal>

            {Boolean(modalConfig) && (
                <ModalSwappableConfigRule
                    show={Boolean(modalConfig)}
                    onClose={() => setModalConfig(null)}
                    organization={organization}
                    configToEdit={modalConfig}
                    event={event}
                    rate={rate}
                    swappableConfigs={swappableConfigs}
                    loadingSwappableConfig={loadingSwappableConfig}
                    syncSwappableConfig={syncSwappableConfig}
                />
            )}

            <Card.FormStep title={t('ticket.rates.ticket-management.title')} rightComponent={<div></div>}>
                <Form>
                    <Message
                        type="default"
                        className={Style['ticket-management-message']}
                        title={t('ticket.rates.ticket-management.title')}
                        links={[
                            {
                                label: t('ticket.rates.ticket-management.message-cta'),
                                onClick: () => history.push(`/ticket/O${organization.id}/personal_spaces`)
                            }
                        ]}
                    >
                        {t('ticket.rates.ticket-management.message-subtitle')}
                    </Message>

                    {viewPermissions.canAllowRateExchange && (
                        <SwappableConfig swappableConfigs={swappableConfigs} setConfigToDelete={setConfigToDelete} setModalConfig={setModalConfig} />
                    )}

                    <TicketTransfer
                        organization={organization}
                        event={event}
                        rate={rate}
                        form={form}
                        setForm={setForm}
                        identitiesOptions={identitiesOptions}
                        templatesOptions={templatesOptions}
                    />
                </Form>
            </Card.FormStep>
        </>
    );
}
